import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import logout from "../helpers/apiFetch/logout";

const SessionContext = createContext();

export const SessionProvider = ({children}) => {
    const [userObj, setUserObj] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [accessToken, setAccessToken] = useState(false);
    const tokenRef = useRef(false);
    const tokenFetching = useRef(false);
    const loggingOut = useRef(false);

    // sets both tokenref and accesstoken
    const setToken = useCallback(async (at) => {
        tokenRef.current = at;
        setAccessToken(at);
    }, []);

    // first render, ensure there is an access token
    useEffect(() => {
        setIsLoading(true);
        
        ensureAccessToken();
    }, []);

    // if the user object changes, force a refresh, means of preventing tampering
    useEffect(() => {
        console.log("useeffect | userObj => ", userObj);
        
        setIsLoading(true);
        refreshAccessToken();
    }, [userObj]);

    // checks if the incoming user object is valid and if it's a dupe of the current
    const checkUserObj = useCallback((newData) => {
        if(!userObj) {
            setUserObj({...newData})
            return;
        }
        
        const tUserObj = JSON.stringify(userObj);
        const tData = JSON.stringify(newData);

        if(tUserObj !== tData) {
            
            setUserObj({...newData});
        }
        setIsLoading(false);
    }, [userObj])

    // fetches an access token provided the refresh is still valid
    const fetchFreshAccessToken = async () => {
        const accessTokenRes = await fetch('/routes/api/sessions/refresh/', {
            method: "POST",
            credentials: "include",
            headers: {
                'Content-Type': "application/json"
            }
        });
        
        if(accessTokenRes.ok) {
            const postRes = await accessTokenRes.json();    
            
            if(postRes?.type && (postRes?.type !== "error" || postRes?.type !== "refreshLanding" || postRes?.type !== "sessionError")) {
                return [true, postRes.accessToken, postRes.payload];
            }
        }
        
        console.log("fetch fresh access token post fetch all not ok  => logout");
        logoutSession();
        return [false, null, null];
    };

    // logs you out of your session
    const logoutSession = useCallback(async () => { 
        if(tokenFetching.current) {
            await tokenFetching.current;
            if(tokenRef.current) {
                return;
            }
        }

        if(loggingOut.current) {
            await loggingOut.current;
            return;
        }
        
        loggingOut.current = (async () => {
            const success = await logout();
            loggingOut.current = null;
            return success;
        })();

        const success = await loggingOut.current;
        if(success) {
            setToken(false);
            setUserObj(false);
        }
        
        if(success !== "switch") {
            window.location.href = "/";
            return;
        }

        await refreshAccessToken();

    }, [setToken]);

    // forcefully refreshes your access token
    const refreshAccessToken = useCallback(async () => {
        // if we are currently fetching a new token
        if(tokenFetching.current) {
            await tokenFetching.current;
            if(tokenRef.current) {
                return [true, tokenRef.current];
            }
            else {
                console.log("refresh access token tokenfetching.current  => logout");
                
                logoutSession();
                return [false, null];
            }
        }

        // start fetching new token
        tokenFetching.current = (async () => {
            const [success, token, payload] = await fetchFreshAccessToken();
            if(success) {
                tokenFetching.current = null;
                setToken(token);
                checkUserObj(payload);
            }
            else {
                tokenFetching.current = null;
            }
        })()

        await tokenFetching.current;
        if(tokenRef.current) {
            setIsLoading(false);
            return [true, tokenRef.current];
        }
        else {
            setIsLoading(false);
            console.log("refresh access token post fetch, tokenref absent  => logout");
            logoutSession();
            return [false, null];
        }
    }, [setToken, checkUserObj, logoutSession]);

    // checks if we have an access token, returns it if true and generates on if false
    const ensureAccessToken = useCallback(async () => {
        if(tokenRef.current){
            setIsLoading(false);
            return [true, tokenRef.current]
        }
        
        if(tokenFetching.current) {
            await tokenFetching.current;
            
            setIsLoading(false);
            if(tokenRef.current) {
                return [true, tokenRef.current];
            }
            else {
                console.log("ensure access token tokenfetching.current  => logout");

                logoutSession();
                return [false, null];
            }
        }
        
        tokenFetching.current = (async () => {
            const [success, token, payload] = await fetchFreshAccessToken();
            if(success) {
                tokenFetching.current = null;
                setToken(token, payload);
                checkUserObj(payload);
            }
            else {
                tokenFetching.current = null;
            }
        })();

        await tokenFetching.current;
        setIsLoading(false);
        
        if(tokenRef.current) {
            return [true, tokenRef.current];
        }
        else {
            console.log("ensure access token post fetch, token ref absent  => logout");
            logoutSession();
            return [false, null];
        }
    }, [setToken, checkUserObj, logoutSession]);

    // log in as user
    const assumeIdentity = useCallback(async (id) => {        
        setIsLoading(false);
        let [present, token] = await ensureAccessToken()
        
        // is the access token present currently or after refresh if needed
        if(present) {
            // check if the token is valid
            const pRes = await fetch("/routes/api/pokeProtected");
            let valid = false;
            // refresh if not
            if(!pRes.ok) {
                [valid, token] = await refreshAccessToken();
            }

            // if the token is present and valid
            if(valid) {
                // initiate login as
                const res = await fetch("/routes/api/sessions/loginAs/", {
                    method: "POST",
                    credentials: "include",
                    headers: {
                        'Content-Type': "application/json",
                        "Authorization": `Bearer ${token}`
                    },
                    body: JSON.stringify({id: id})
                });
        
                // if the request didn't fail at some point
                if(res.ok) {
                    // process data and assume identity of the user
                    const data = await res.json();
                    
                    if(data.type === "success") {
                        setUserObj(userObj);
                        setAccessToken(accessToken);
                        // tokenRef.current = tokenRef.current;
                        tokenRef.current = false;

                        const [ok, t] = await refreshAccessToken();
                        
                        if(ok) {
                            return;
                        }
                    }
                }
            }
        }
        logoutSession();
    }, [setToken]);

    /**
     * 
     * @param {Array} targetACs 
     * @param {Bool} strict 
     * @return {Bool}
     * 
     * Check access control with passed array of access control strings
     * 
     * ## Target AC's
     * - array of access control names to compare
     * 
     * ## Strict
     * - default = true
     * - requires all targetAC's are found instead of some
     */
    const validateAccessControl = useCallback(({targetACs, strict = true, overrideMaster}) => {
        try {
            
            if(userObj?.access_control_names?.length) {
                if(!overrideMaster && userObj.access_control_names.includes("*")) {
                    return true;
                }

                const tacLength = targetACs.filter(tac => userObj.access_control_names.includes(tac))?.length;
                if(tacLength && !strict) return true;
                if(tacLength === targetACs.length && strict) return true;
            }

            return false;
        } catch (error) {
            console.log(error);
            return false
        }
    }, [userObj])

    return (
        <SessionContext.Provider value={{userObj: ((!isLoading) ? (userObj) : {}), assumeIdentity, isLoading, ensureAccessToken, refreshAccessToken, getAccessToken: () => tokenRef.current, logout: logoutSession, validateAccessControl}}>
            {children}
        </SessionContext.Provider>
    );
};

export const useAuth = () => useContext(SessionContext);
